import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MapPin, Mail, Phone, Plus, Pencil, Trash2, Globe, User, Target,
  StickyNote, CalendarPlus, ClipboardCheck, ShieldOff, ShieldCheck, CheckCircle2,
  ChevronDown, ChevronUp, Navigation,
} from "lucide-react";
import {
  orgsApi, locationsApi, contactsApi, opportunitiesApi, activitiesApi,
  visitsApi, tasksApi, suppressionsApi, productsApi, apiErrorMessage,
} from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, VenueBadge, labelize } from "@/components/Badges";
import { EmptyState } from "@/components/EmptyState";
import { OrgFormDialog } from "@/components/forms/OrgFormDialog";
import { LocationFormDialog } from "@/components/forms/LocationFormDialog";
import { ContactFormDialog } from "@/components/forms/ContactFormDialog";
import { OpportunityFormDialog } from "@/components/forms/OpportunityFormDialog";
import { TaskFormDialog } from "@/components/forms/TaskFormDialog";
import { VisitPlanDialog } from "@/components/forms/VisitPlanDialog";
import { TripSuggestionPanel } from "@/components/TripSuggestionPanel";
import { VisitOutcomeDialog } from "@/components/forms/VisitOutcomeDialog";
import { FollowUpCard } from "@/components/FollowUpCard";
import { NoteDialog } from "@/components/forms/NoteDialog";
import { SuppressionDialog } from "@/components/forms/SuppressionDialog";
import { AssignDialog } from "@/components/forms/AssignDialog";
import { ProductOpportunities } from "@/components/ProductOpportunities";
import { RecommendedChannel } from "@/components/RecommendedChannel";
import { UserCheck } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManageLeads, user, isManager } = useAuth();

  const [org, setOrg] = useState(null);
  const [locations, setLocations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [opps, setOpps] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [visits, setVisits] = useState([]);
  const [activities, setActivities] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppression, setSuppression] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editOrg, setEditOrg] = useState(false);
  const [locDialog, setLocDialog] = useState(false);
  const [contactDialog, setContactDialog] = useState(false);
  const [oppDialog, setOppDialog] = useState(false);
  const [taskDialog, setTaskDialog] = useState(false);
  const [visitDialog, setVisitDialog] = useState(false);
  const [noteDialog, setNoteDialog] = useState(false);
  const [suppDialog, setSuppDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [outcomeVisit, setOutcomeVisit] = useState(null);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [geocoding, setGeocoding] = useState(null);

  const geocodeLocation = async (locId) => {
    setGeocoding(locId);
    try {
      const { data } = await locationsApi.geocode(locId);
      if (data.status === "geocoded") toast.success("Address geocoded via Postcodes.io");
      else if (data.status === "already_geocoded") toast.info("Location already has coordinates");
      else if (data.status === "missing_postcode") toast.error("No valid UK postcode on this location");
      else toast.error("Postcodes.io could not resolve this address");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setGeocoding(null); }
  };

  const load = useCallback(async () => {
    try {
      const [o, l, c, op, t, v, a, p, s] = await Promise.all([
        orgsApi.get(id), locationsApi.listForOrg(id), contactsApi.listForOrg(id),
        opportunitiesApi.listForOrg(id), tasksApi.listForOrg(id), visitsApi.listForOrg(id),
        activitiesApi.listForOrg(id), productsApi.list({ active_only: true }), suppressionsApi.get(id),
      ]);
      setOrg(o.data); setLocations(l.data.items); setContacts(c.data.items);
      setOpps(op.data.items); setTasks(t.data.items); setVisits(v.data.items);
      setActivities(a.data.items); setProducts(p.data.items); setSuppression(s.data.active);
    } catch (e) {
      toast.error(apiErrorMessage(e));
      navigate("/leads");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const deleteOrg = async () => {
    if (!window.confirm("Delete this organisation and all its records?")) return;
    await orgsApi.remove(id);
    toast.success("Organisation deleted");
    navigate("/leads");
  };
  const liftSuppression = async () => { await suppressionsApi.lift(id); toast.success("Suppression lifted"); load(); };
  const completeTask = async (tid) => { await tasksApi.complete(tid); toast.success("Task completed"); load(); };
  const planned = visits.find((v) => v.state === "planned" || v.state === "arrived");

  if (loading || !org) {
    return <div className="mx-auto max-w-3xl px-4 py-6"><div className="h-32 animate-pulse rounded-xl border border-slate-200 bg-white" /></div>;
  }

  const oppByProduct = {};
  opps.forEach((o) => { (oppByProduct[o.product_code] = oppByProduct[o.product_code] || []).push(o); });
  const shownActivities = showAllActivity ? activities : activities.slice(0, 8);

  return (
    <div className="mx-auto max-w-3xl px-4 py-4 pb-28 md:py-8">
      <button data-testid="lead-back" onClick={() => navigate("/leads")} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Leads
      </button>

      {suppression && (
        <div data-testid="suppression-banner" className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-3">
          <div className="flex items-center gap-2 text-sm text-red-800">
            <ShieldOff className="h-4 w-4 shrink-0" />
            <span><strong>Suppressed</strong> · {labelize(suppression.suppression_type)}{suppression.reason ? ` — ${suppression.reason}` : ""}</span>
          </div>
          {canManageLeads && <button data-testid="lift-suppression" onClick={liftSuppression} className="shrink-0 rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-100">Lift</button>}
        </div>
      )}

      {/* Org summary */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="lead-summary">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">{org.legal_name}</h1>
            {org.trading_name && <p className="text-sm text-slate-500">{org.trading_name}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusBadge status={org.status} />
              {org.organisation_type && <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium capitalize text-slate-600">{labelize(org.organisation_type)}</span>}
            </div>
            {org.website && <a href={org.website} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-teal-700 hover:underline"><Globe className="h-3.5 w-3.5" /> {org.website}</a>}
          </div>
          {canManageLeads && (
            <div className="flex shrink-0 gap-1">
              <button data-testid="lead-edit" onClick={() => setEditOrg(true)} className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><Pencil className="h-4 w-4" /></button>
              {!suppression && <button data-testid="suppress-org" onClick={() => setSuppDialog(true)} className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-amber-600 hover:bg-amber-50"><ShieldCheck className="h-4 w-4" /></button>}
              <button data-testid="lead-delete" onClick={deleteOrg} className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          )}
        </div>
        {/* Assignment row */}
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5" data-testid="lead-assignment-row">
          <span className="flex items-center gap-2 text-sm text-slate-600">
            <UserCheck className="h-4 w-4 text-teal-600" />
            {org.assigned_user_id
              ? (org.assigned_user_id === user?.id ? "Assigned to you" : "Assigned")
              : <span className="font-semibold text-amber-700">Unclaimed</span>}
          </span>
          {!org.assigned_user_id && !isManager && (
            <button data-testid="claim-lead" onClick={async () => { const { assignApi } = await import("@/api/client"); try { await assignApi.claim(id); toast.success("Lead claimed"); load(); } catch (e) { toast.error(apiErrorMessage(e)); } }}
              className="shrink-0 rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-100">Claim</button>
          )}
          {(isManager || org.owner_user_id === user?.id || org.assigned_user_id === user?.id) && (
            <button data-testid="assign-lead" onClick={() => setAssignDialog(true)}
              className="shrink-0 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100">Assign / Reassign</button>
          )}
        </div>
        <RecommendedChannel orgId={id} />
      </div>

      {/* Opportunities by product (all 4 slots) */}
      <Section title="Opportunities" count={opps.length}>
        <div className="space-y-2" data-testid="opportunities-by-product">
          {products.map((p) => {
            const list = oppByProduct[p.code] || [];
            return (
              <div key={p.code} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`product-slot-${p.code}`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-sm font-bold text-slate-800">{p.name}</span>
                  {canManageLeads && <button data-testid={`add-opp-${p.code}`} onClick={() => setOppDialog(true)} className="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-100"><Plus className="h-3 w-3" /> Add</button>}
                </div>
                {list.length === 0 ? <p className="mt-1 text-xs text-slate-400">No opportunity — cross-sell available.</p> :
                  <div className="mt-2 space-y-1.5">{list.map((o) => (
                    <div key={o.id} data-testid={`opp-${o.id}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                      <span className="flex items-center gap-2 text-sm"><Target className="h-3.5 w-3.5 text-teal-600" /><span className="font-medium text-slate-700">{o.stage}</span>{o.probability != null && <span className="text-xs text-slate-400">{o.probability}%</span>}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${o.won_lost_state === "won" ? "bg-emerald-100 text-emerald-700" : o.won_lost_state === "lost" ? "bg-rose-100 text-rose-700" : "bg-blue-100 text-blue-700"}`}>{o.won_lost_state}</span>
                    </div>
                  ))}</div>}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Product Opportunities (Stage 2 — deterministic fit) */}
      <ProductOpportunities orgId={id} />

      {/* Tasks */}
      <Section title="Follow-ups" count={tasks.length} onAdd={canManageLeads ? () => setTaskDialog(true) : null} addTestid="add-task-btn">
        {tasks.length === 0 ? <EmptyState testid="tasks-empty" icon={ClipboardCheck} title="No follow-ups" description="Add a task to stay on top of this lead." /> :
          <div className="space-y-2">{tasks.map((t) => (
            <div key={t.id} data-testid={`task-row-${t.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">{t.title}</p>
                <p className="text-xs text-slate-400">Due {fmt(t.due_at)} · <span className={t.derived_state === "overdue" ? "font-semibold text-rose-600" : ""}>{t.derived_state.replace(/_/g, " ")}</span></p>
              </div>
              {canManageLeads && t.status === "open" && <button data-testid={`complete-task-${t.id}`} onClick={() => completeTask(t.id)} className="shrink-0 rounded-md border border-emerald-200 bg-emerald-50 p-1.5 text-emerald-600 hover:bg-emerald-100"><CheckCircle2 className="h-4 w-4" /></button>}
            </div>
          ))}</div>}
      </Section>

      {/* Visits */}
      <Section title="Visits" count={visits.length} onAdd={canManageLeads ? () => setVisitDialog(true) : null} addTestid="add-visit-btn">
        {visits.length === 0 ? <EmptyState testid="visits-empty" icon={MapPin} title="No visits" description="Plan a visit to a venue." /> :
          <div className="space-y-2">{visits.map((v) => (
            <div key={v.id} className="space-y-2">
              <div data-testid={`visit-row-${v.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">Visit · {fmt(v.planned_at)}</p>
                  <p className="text-xs text-slate-400 capitalize">{v.state}{v.outcome_code ? ` · ${labelize(v.outcome_code)}` : v.outcome ? ` · ${labelize(v.outcome)}` : ""}</p>
                </div>
                {canManageLeads && <button data-testid={`record-outcome-${v.id}`} onClick={() => setOutcomeVisit(v)} className="shrink-0 rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-100">{v.suggested_next_action ? "Change outcome" : "Record outcome"}</button>}
              </div>
              {canManageLeads && (v.visit_type === "field" || !v.visit_type) && v.state === "planned"
                && new Date(v.planned_at) > new Date() && (
                <TripSuggestionPanel visitId={v.id} onApproved={load} />
              )}
              {v.suggested_next_action && <FollowUpCard visit={v} canAct={canManageLeads} onAction={load} />}
            </div>
          ))}</div>}
      </Section>

      {/* Locations */}
      <Section title="Locations" count={locations.length} onAdd={canManageLeads ? () => setLocDialog(true) : null} addTestid="add-location-btn">
        {locations.length === 0 ? <EmptyState testid="locations-empty" icon={MapPin} title="No locations" description="Add a venue to place this lead on the map." /> :
          <div className="space-y-2">{locations.map((l) => (
            <div key={l.id} data-testid={`location-row-${l.id}`} className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="truncate text-sm font-semibold text-slate-800">{l.name}</p>
              <p className="text-xs text-slate-400">{[l.address_line1, l.city, l.postcode].filter(Boolean).join(", ") || "No address"}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <VenueBadge venueType={l.venue_type} />
                {l.latitude != null ? (
                  <span className="inline-flex items-center gap-1 text-xs text-teal-700" data-testid={`location-mapped-${l.id}`}>
                    <MapPin className="h-3 w-3" /> Mapped
                    {l.geo_provenance?.provider === "postcodes_io" && l.geo_provenance?.resolved_at && (
                      <span className="text-slate-400">· via Postcodes.io at {new Date(l.geo_provenance.resolved_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
                    )}
                  </span>
                ) : (l.postcode || l.address_line1) ? (
                  <button data-testid={`location-geocode-${l.id}`} onClick={() => geocodeLocation(l.id)} disabled={geocoding === l.id}
                    className="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-700 hover:bg-teal-100 disabled:opacity-50">
                    <Navigation className="h-3 w-3" /> {geocoding === l.id ? "Geocoding…" : "Geocode address"}
                  </button>
                ) : (
                  <span className="text-xs text-slate-400">No coordinates</span>
                )}
              </div>
            </div>
          ))}</div>}
      </Section>

      {/* Contacts */}
      <Section title="Contacts" count={contacts.length} onAdd={canManageLeads ? () => setContactDialog(true) : null} addTestid="add-contact-btn">
        {contacts.length === 0 ? <EmptyState testid="contacts-empty" icon={User} title="No contacts" description="Add the people who run this organisation." /> :
          <div className="space-y-2">{contacts.map((c) => (
            <div key={c.id} data-testid={`contact-row-${c.id}`} className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="truncate text-sm font-semibold text-slate-800">{c.full_name}</p>
              <p className="text-xs text-slate-400">{[c.job_title, c.role_category && labelize(c.role_category)].filter(Boolean).join(" · ")}</p>
              <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-slate-600">
                {c.business_email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{c.business_email}</span>}
                {c.business_phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{c.business_phone}</span>}
              </div>
            </div>
          ))}</div>}
      </Section>

      {/* Activity timeline */}
      <Section title="Activity timeline" count={activities.length}>
        {activities.length === 0 ? <EmptyState testid="activity-empty" icon={StickyNote} title="No activity yet" description="Notes, calls, visits and system events appear here." /> :
          <>
            <div className="space-y-2" data-testid="activity-timeline">{shownActivities.map((a) => (
              <div key={a.id} data-testid={`activity-${a.id}`} className="flex gap-3 rounded-xl border border-slate-100 bg-white p-3">
                <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${a.is_system ? "bg-slate-300" : "bg-teal-500"}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">{a.summary}</p>
                  {a.content && <p className="text-xs text-slate-500">{a.content}</p>}
                  <p className="text-xs text-slate-400">{a.activity_type.replace(/_/g, " ")} · {fmt(a.created_at)}{a.corrects_activity_id ? " · correction" : ""}</p>
                </div>
              </div>
            ))}</div>
            {activities.length > 8 && (
              <button data-testid="toggle-activity" onClick={() => setShowAllActivity((s) => !s)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-teal-700">
                {showAllActivity ? <><ChevronUp className="h-3.5 w-3.5" /> Show less</> : <><ChevronDown className="h-3.5 w-3.5" /> Show all {activities.length}</>}
              </button>
            )}
          </>}
      </Section>

      {/* One-handed sticky action bar */}
      {canManageLeads && (
        <div data-testid="lead-action-bar" className="fixed inset-x-0 bottom-16 z-30 border-t border-slate-200 bg-white/95 px-3 py-2 backdrop-blur md:bottom-0">
          <div className="mx-auto flex max-w-3xl items-center gap-2 overflow-x-auto">
            <ActionBtn testid="action-note" icon={StickyNote} label="Note" onClick={() => setNoteDialog(true)} />
            <ActionBtn testid="action-plan-visit" icon={CalendarPlus} label="Visit" onClick={() => setVisitDialog(true)} />
            <ActionBtn testid="action-follow-up" icon={ClipboardCheck} label="Follow-up" onClick={() => setTaskDialog(true)} />
            <ActionBtn testid="action-opportunity" icon={Target} label="Opportunity" onClick={() => setOppDialog(true)} />
            {planned && <ActionBtn testid="action-record-outcome" icon={CheckCircle2} label="Outcome" primary onClick={() => setOutcomeVisit(planned)} />}
          </div>
        </div>
      )}

      <OrgFormDialog open={editOrg} onOpenChange={setEditOrg} existing={org} onSaved={load} />
      <LocationFormDialog open={locDialog} onOpenChange={setLocDialog} orgId={id} onSaved={load} />
      <ContactFormDialog open={contactDialog} onOpenChange={setContactDialog} orgId={id} onSaved={load} />
      <OpportunityFormDialog open={oppDialog} onOpenChange={setOppDialog} orgId={id} onSaved={load} />
      <TaskFormDialog open={taskDialog} onOpenChange={setTaskDialog} orgId={id} onSaved={load} />
      <VisitPlanDialog open={visitDialog} onOpenChange={setVisitDialog} orgId={id} locations={locations} contacts={contacts} onSaved={load} />
      <NoteDialog open={noteDialog} onOpenChange={setNoteDialog} orgId={id} onSaved={load} />
      <SuppressionDialog open={suppDialog} onOpenChange={setSuppDialog} orgId={id} onSaved={load} />
      <VisitOutcomeDialog open={!!outcomeVisit} onOpenChange={(v) => !v && setOutcomeVisit(null)} visit={outcomeVisit} onSaved={load} />
      <AssignDialog open={assignDialog} onOpenChange={setAssignDialog} entityType="organisation" entityId={id} currentUserId={org.assigned_user_id} onSaved={load} />
    </div>
  );
}

function ActionBtn({ testid, icon: Icon, label, onClick, primary }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex min-h-[48px] flex-1 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg px-3 text-[11px] font-semibold transition-colors ${
        primary ? "bg-teal-700 text-white hover:bg-teal-800" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function Section({ title, count, onAdd, addTestid, children }) {
  return (
    <section className="mt-6">
      <div className="mb-2.5 flex items-center justify-between">
        <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">{title} <span className="text-slate-300">({count})</span></h2>
        {onAdd && <button data-testid={addTestid} onClick={onAdd} className="inline-flex items-center gap-1 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-100"><Plus className="h-3.5 w-3.5" /> Add</button>}
      </div>
      {children}
    </section>
  );
}
